import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { hasAnalyticsConsent } from '../hooks/useAnalytics';
import api from '../utils/apiClient';

const CartContext = createContext();

// Analytics tracking for cart events
const trackCartAnalytics = async (eventType, item, cartTotal, cartItemsCount, orderId = null) => {
  if (!hasAnalyticsConsent()) return;
  
  const visitorId = document.cookie.match(/bc_visitor_id=([^;]+)/)?.[1];
  const sessionId = sessionStorage.getItem('bc_session_id');
  
  if (!visitorId || !sessionId) return;
  
  try {
    await api.post('/analytics/track/cart', {
      visitor_id: visitorId,
      session_id: sessionId,
      event_type: eventType,
      item_id: item?.item_id || item?.id,
      item_name: item?.name,
      item_category: item?.category,
      item_price: item?.price,
      quantity: item?.quantity || 1,
      cart_total: cartTotal,
      cart_items_count: cartItemsCount,
      order_id: orderId
    });
  } catch (error) {
    console.error('Cart analytics error:', error);
  }
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('bulaccino-cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('bulaccino-cart', JSON.stringify(cart));
  }, [cart]);

  // Generate unique cart item key based on item + customizations
  const getCartItemKey = (item, selectedSize, modifiers, removedIngredients, extraIngredients, addOns) => {
    let key = item.id;
    if (selectedSize) key += `-${selectedSize}`;
    if (modifiers && modifiers.length > 0) {
      const modKey = modifiers.map(m => `${m.modifier_name}:${m.selected_option}`).sort().join('|');
      key += `-${modKey}`;
    }
    if (removedIngredients && removedIngredients.length > 0) {
      key += `-no:${removedIngredients.sort().join(',')}`;
    }
    if (extraIngredients && extraIngredients.length > 0) {
      key += `-extra:${extraIngredients.map(e => e.name).sort().join(',')}`;
    }
    if (addOns && addOns.length > 0) {
      key += `-add:${addOns.map(a => a.name).sort().join(',')}`;
    }
    return key;
  };

  const addToCart = (item, selectedSize = null, modifiers = null, specialNotes = '', removedIngredients = null, extraIngredients = null, addOns = null) => {
    setCart(prev => {
      // Calculate final price based on size and modifiers
      let finalPrice = item.price;
      
      if (selectedSize && item.sizes) {
        const sizeData = item.sizes.find(s => s.name === selectedSize);
        if (sizeData) finalPrice = sizeData.price;
      }
      
      if (modifiers) {
        modifiers.forEach(mod => {
          finalPrice += mod.price_adjustment || 0;
        });
      }
      
      // Add extra ingredients cost
      if (extraIngredients) {
        extraIngredients.forEach(ing => {
          finalPrice += ing.price || 0;
        });
      }
      
      // Add add-ons cost
      if (addOns) {
        addOns.forEach(addon => {
          finalPrice += addon.price || 0;
        });
      }
      
      const cartKey = getCartItemKey(item, selectedSize, modifiers, removedIngredients, extraIngredients, addOns);
      const existing = prev.find(i => i.cart_key === cartKey);
      
      let newCart;
      if (existing) {
        newCart = prev.map(i => 
          i.cart_key === cartKey 
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      } else {
        newCart = [...prev, {
          cart_key: cartKey,
          item_id: item.id,
          name: item.name,
          price: finalPrice,
          base_price: item.price,
          quantity: 1,
          image: item.image,
          category: item.category,
          selected_size: selectedSize,
          modifiers: modifiers || [],
          special_instructions: specialNotes || null,
          removed_ingredients: removedIngredients || [],
          extra_ingredients: extraIngredients || [],
          add_ons: addOns || []
        }];
      }
      
      // Track add to cart event
      const newTotal = newCart.reduce((sum, i) => sum + i.price * i.quantity, 0);
      const newCount = newCart.reduce((sum, i) => sum + i.quantity, 0);
      trackCartAnalytics('add_to_cart', { ...item, price: finalPrice, category: item.category }, newTotal, newCount);
      
      return newCart;
    });
  };

  const removeFromCart = (cartKey) => {
    setCart(prev => {
      const itemToRemove = prev.find(i => i.cart_key === cartKey);
      const newCart = prev.filter(i => i.cart_key !== cartKey);
      
      // Track remove from cart
      if (itemToRemove) {
        const newTotal = newCart.reduce((sum, i) => sum + i.price * i.quantity, 0);
        const newCount = newCart.reduce((sum, i) => sum + i.quantity, 0);
        trackCartAnalytics('remove_from_cart', itemToRemove, newTotal, newCount);
      }
      
      return newCart;
    });
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity < 1) {
      removeFromCart(cartKey);
      return;
    }
    setCart(prev => prev.map(i => 
      i.cart_key === cartKey ? { ...i, quantity } : i
    ));
  };

  const updateSpecialInstructions = (cartKey, instructions) => {
    setCart(prev => prev.map(i => 
      i.cart_key === cartKey ? { ...i, special_instructions: instructions } : i
    ));
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Track checkout events
  const trackCheckoutStart = useCallback(() => {
    trackCartAnalytics('begin_checkout', null, cartTotal, cartCount);
  }, [cartTotal, cartCount]);

  const trackPurchase = useCallback((orderId) => {
    trackCartAnalytics('purchase', null, cartTotal, cartCount, orderId);
  }, [cartTotal, cartCount]);

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateSpecialInstructions,
      clearCart,
      trackCheckoutStart,
      trackPurchase,
      cartTotal,
      cartCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
